import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useShell } from './shell';

export type TrackParams = Record<string, unknown>;

/** The activity log's page type for a path: home, tips, high_probability, or the first segment. */
export function pageTypeOf(pathname: string): string {
  if (/^\/(\d{8})?\/?$/.test(pathname)) return 'home';
  if (pathname.startsWith('/high-probability')) return 'high_probability';
  if (/^\/[a-z]+\/[^/]+\/\d{8}/i.test(pathname)) return 'tips';
  return pathname.split('/')[1] || 'home';
}

/**
 * New-look event names carry a _v2 suffix (page_view_v2, ui_toggle_v2 ...) so
 * reports separate them from classic's by name. ui_version_switch is about
 * moving between the two, so it keeps one name from either side.
 */
export const v2 = (event: string): string => (event === 'ui_version_switch' ? event : `${event}_v2`);

/** On a tips page, the meeting's track code from the address, so every event there names it. */
const trackOf = (pathname: string): TrackParams => {
  const m = /^\/[a-z]+\/([^/]+)\/\d{8}/i.exec(pathname);
  return m ? { trackName: m[1] } : {};
};

/**
 * Event tracking for the new look. Every event carries the page type and
 * uiVersion 'new' (classic pages never send uiVersion), so reports can split
 * classic from new and see which controls get used. Vocabulary:
 * - content_expand / content_collapse { contentType } for anything that opens
 * - ui_toggle { control, value } for switches and segmented controls
 * - ui_click { target } for links and buttons that go somewhere
 */
export function useTrack() {
  const { track } = useShell();
  const { pathname } = useLocation();
  return useCallback(
    (event: string, params: TrackParams = {}) =>
      track(v2(event), { pageType: pageTypeOf(pathname), ...trackOf(pathname), uiVersion: 'new', ...params }),
    [track, pathname],
  );
}

/** A section opening or closing, in the tips page's long-standing event names. */
export function useTrackExpand() {
  const t = useTrack();
  return useCallback(
    (contentType: string, open: boolean, params: TrackParams = {}) =>
      t(open ? 'content_expand' : 'content_collapse', { contentType, ...params }),
    [t],
  );
}

/**
 * Splits shared-UI params into the activity log's columns (page type, track,
 * race, date) and its metadata JSON, flattening a nested `metadata` param.
 */
export function toActivity(params: TrackParams = {}) {
  const { pageType, trackName, raceNumber, selectedDate, metadata, ...rest } = params;
  return {
    pageType: pageType as string | undefined,
    trackName: trackName as string | undefined,
    raceNumber: typeof raceNumber === 'number' ? raceNumber : undefined,
    ...(typeof selectedDate === 'string' ? { selectedDate } : {}),
    metadata: { ...(metadata && typeof metadata === 'object' ? (metadata as TrackParams) : {}), ...rest },
  };
}
